import { Wallet, Upload } from 'lucide-react';
import { useIncomeProfiles } from '../hooks/useStatements';
import type { Case, IncomeProfile } from '../types';
import { Card } from './ui';
import { money } from '../lib/money';

interface IncomeCardProps {
  caseId: string;
  caseData?: Case;
}

const PAY_PERIOD_LABELS: Record<string, string> = {
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  semimonthly: 'Semimonthly',
  monthly: 'Monthly',
  annual: 'Annual',
};

interface Row {
  label: string;
  value: string;
}

function profileRows(p: IncomeProfile): Row[] {
  const rows: Row[] = [];
  if (p.employer) rows.push({ label: 'Employer', value: p.employer });
  if (p.pay_period) {
    rows.push({ label: 'Pay Period', value: PAY_PERIOD_LABELS[p.pay_period] ?? p.pay_period });
  }
  if (p.gross_pay !== null) rows.push({ label: 'Gross (per period)', value: money(p.gross_pay) });
  if (p.net_pay !== null) rows.push({ label: 'Net (per period)', value: money(p.net_pay) });
  if (p.ytd_gross !== null) rows.push({ label: 'YTD Gross', value: money(p.ytd_gross) });
  if (p.retirement_contributions !== null) {
    rows.push({ label: 'Retirement', value: money(p.retirement_contributions) });
  }
  if (p.annual_gross_income !== null) {
    rows.push({ label: 'Annual Income', value: money(p.annual_gross_income) });
  }
  if (p.tax_year !== null) rows.push({ label: 'Tax Year', value: String(p.tax_year) });
  if (p.filing_status) rows.push({ label: 'Filing Status', value: p.filing_status });
  return rows;
}

function SpouseColumn({ name, profile }: { name: string; profile?: IncomeProfile }) {
  const rows = profile ? profileRows(profile) : [];
  return (
    <Card>
      <h3 className="t-card mb-2 text-green-900">{name}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-faint">
          No income recorded yet. Upload a pay stub or tax return and confirm it to fill this in.
        </p>
      ) : (
        <div className="text-sm">
          {rows.map((r) => (
            <div
              key={r.label}
              className="flex items-start gap-2 border-t border-border-subtle py-2 first:border-t-0"
            >
              <span className="t-eyebrow w-40 shrink-0">{r.label}</span>
              <span className="text-green-900">{r.value}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function IncomeCard({ caseId, caseData }: IncomeCardProps) {
  const { data: profiles = [] } = useIncomeProfiles(caseId);
  const bySpouse = new Map(profiles.map((p) => [p.spouse, p]));
  const clientName = caseData?.client_name || 'Client';
  const opposingName = caseData?.opposing_party_name || 'Opposing Party';

  return (
    <div className="space-y-4">
      <div>
        <h2 className="t-section flex items-center gap-2 text-green-900">
          <Wallet size={20} />
          Income
        </h2>
        <p className="mt-1 text-sm text-ink-faint">
          Per-spouse income from pay stubs and tax returns. Auto-filled when you confirm an
          income document on the Upload tab.
        </p>
      </div>

      {profiles.length === 0 && (
        <Card>
          <div className="flex items-start gap-3 p-1">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-clay-100 text-clay-700">
              <Upload size={20} />
            </div>
            <div>
              <p className="font-medium text-green-900">No income recorded yet</p>
              <p className="mt-1 text-sm text-ink-soft">
                Drop a pay stub or tax return on the Upload tab. We'll read the income and let you
                assign it to a spouse here.
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SpouseColumn name={clientName} profile={bySpouse.get('client')} />
        <SpouseColumn name={opposingName} profile={bySpouse.get('opposing')} />
      </div>
    </div>
  );
}
